// src/types/privacyRequest.ts

export type PrivacyRequestType = 'know' | 'delete' | 'correct' | 'portability' | 'opt-out' | 'limit';
export type PrivacyRequestStatus = 'pending' | 'verifying' | 'in_progress' | 'completed' | 'rejected' | 'expired';
export type VerificationMethod = 'email' | 'manual' | 'authenticated';

export interface PrivacyRequest {
  id?: string; // Document ID
  userId: string; // Firebase Auth UID
  email: string;
  requestType: PrivacyRequestType;
  status: PrivacyRequestStatus;
  
  // Request details
  description?: string; // Optional details from user
  requestedData?: string[]; // Specific fields if applicable
  
  // Verification
  verificationToken?: string;
  verifiedAt?: any; // Firestore timestamp
  verificationMethod: VerificationMethod;
  
  // Processing
  submittedAt: any; // Firestore timestamp
  deadline: any; // Firestore timestamp (submittedAt + 45 days)
  processedAt?: any; // Firestore timestamp
  processedBy?: string; // Admin UID
  
  // Response data (for "know" requests)
  exportedDataUrl?: string; // Secure download link
  dataExportedAt?: any; // Firestore timestamp
  
  // Deletion details (for "delete" requests)
  deletedCollections?: string[];
  deletionExceptions?: string[]; // Reasons why some data couldn't be deleted
  softDeleted?: boolean; // If hard deletion wasn't possible
  
  // Correction details (for "correct" requests)
  corrections?: {
    collection: string;
    field: string;
    oldValue: any;
    newValue: any;
  }[];
  
  // Opt-out details
  optOutPreferences?: {
    sale: boolean;
    sharing: boolean;
    marketing: boolean;
  };
  
  // Audit
  notes?: string[]; // Internal notes
  rejectionReason?: string;
  createdAt: any; // Firestore timestamp
  updatedAt: any; // Firestore timestamp
}

export interface UserPrivacyPreferences {
  userId: string; // Document ID = userId
  optOutSale: boolean;
  optOutSharing: boolean;
  optOutMarketing: boolean;
  limitSensitiveInfo: boolean;
  updatedAt: any; // Firestore timestamp
}

export interface ExportedUserData {
  exportMetadata: {
    exportedAt: string;
    userId: string;
    email: string;
    requestId: string;
  };
  profile?: any;
  businessData?: any;
  tasks?: any[];
  documents?: any[];
  entityForms?: {
    drafts?: any[];
    submissions?: any[];
  };
  subscriptions?: any[];
  auditLogs?: any[];
}

export const REQUEST_TYPES = [
  {
    id: 'know' as PrivacyRequestType,
    title: 'Request to Know',
    description: 'Get a copy of the personal information we have about you'
  },
  {
    id: 'delete' as PrivacyRequestType,
    title: 'Request to Delete',
    description: 'Request deletion of your personal information (subject to legal retention requirements)'
  },
  {
    id: 'correct' as PrivacyRequestType,
    title: 'Request to Correct',
    description: 'Request correction of inaccurate personal information'
  },
  {
    id: 'portability' as PrivacyRequestType,
    title: 'Request Data Portability',
    description: 'Export your data in a machine-readable format (JSON/CSV)'
  },
  {
    id: 'opt-out' as PrivacyRequestType,
    title: 'Opt-Out of Sale/Sharing',
    description: 'Opt-out of the sale or sharing of your personal information'
  },
  {
    id: 'limit' as PrivacyRequestType,
    title: 'Limit Sensitive Information',
    description: 'Limit use of your sensitive personal information'
  }
];

// Collections that CAN be deleted
export const DELETABLE_COLLECTIONS = [
  'users',
  'clients', 
  'entityFormationDrafts',
  'documents'
];

// Fields/collections that MUST be retained for legal compliance
export const RETAINED_DATA = {
  billingRecords: 7, // years
  financialRecords: 7, // years
  taxDocuments: 7, // years
  auditLogs: 6 // years
};
